// ---------------------------------------------------------------------------
// Terminology selection model — pure diagnosis → renderable-model selector.
//
// Plan: docs/superpowers/plans/terminology-selection-ui-plan.md (Task 1).
//
// The decompose response may carry a `terminologyDiagnosis`
// (`functions/api/knowledge/terminology-diagnosis.ts`) naming which of the
// prompt's own domain terms are undefined coogo terminology, and — for the
// ones that are not — which DEFINED terms look similar. That diagnosis is
// descriptive; it does not by itself decide whether to show the user
// anything. This module is that decision, made once and made purely: given a
// diagnosis, is there a genuine choice for the user to make, and if so, what
// exactly should be offered?
//
// Operator principle (load-bearing, see the plan's Context section): the
// selection UI is an EXCEPTION path for when quality already failed — NOT
// per-request UX. It must appear ONLY when there is a real choice (an
// undefined term with at least one similar DEFINED candidate). Any other
// diagnosis — fully defined, or undefined-but-nothing-similar — is the
// normal silently-resolved path and must produce no model at all.
//
// Constraints this file keeps (plan §Global Constraints, Task 1):
//   - pure and deterministic: no React, no store, no network, no LLM; same
//     diagnosis in -> identical (structurally-equal) model out, always.
//   - never invents: every term/id/kind/score in the output model is copied
//     verbatim from the input diagnosis — nothing here is synthesized.
//   - bounded: at most 5 terms, at most 4 candidates per term. The diagnosis
//     already enforces these caps (MAX_UNDEFINED_TERMS /
//     MAX_CANDIDATES_PER_TERM in terminology-diagnosis.ts); the caps below
//     are defense-in-depth so this function's OWN output contract does not
//     silently depend on the caller having upheld them.
//   - scope is carried through, not invented: a diagnosis resolves against
//     `platform` or a validated `app:<slug>` scope, and that is the scope a
//     selection from it would declare. When the diagnosis scope is
//     "platform" there is no app scope to declare — this function still
//     returns a model (candidates remain visible), it just reports "platform"
//     as-is rather than fabricating an app scope. Task 3 decides whether a
//     platform-scope selection actually writes anything.
//   - C2 (bootstrap-only cross-scope candidate surfacing): a diagnosis
//     candidate now carries its OWN `scope`, which may differ from the
//     diagnosis's top-level `scope` (e.g. the diagnosis resolved against
//     "platform" but a candidate came from bootstrap-searching
//     "app:wonbanking"). That per-candidate scope — not `term.scope` — is
//     what a pick of THAT candidate would declare; it is copied through
//     verbatim here (falling back to the diagnosis's own scope only when a
//     candidate doesn't carry one, for backward compat with a pre-C2
//     diagnosis shape).

import type { TerminologyDiagnosis } from '../../../../functions/api/knowledge/terminology-diagnosis'
import type { TerminologyKind } from '@cogo/validator/terminology'

// Mirrors terminology-diagnosis.ts's own MAX_UNDEFINED_TERMS /
// MAX_CANDIDATES_PER_TERM. The diagnosis already bounds its input to these
// values; these are a second, independent guarantee on this function's own
// output shape.
const MAX_TERMS = 5
const MAX_CANDIDATES_PER_TERM = 4

export interface TerminologySelectionCandidate {
  id: string
  kind: TerminologyKind
  /** 0..1, copied verbatim from the diagnosis candidate — never invented. */
  score: number
  /**
   * The terminology-index scope THIS candidate's entry belongs to
   * ("platform" or "app:<slug>", C2) — copied verbatim from the diagnosis
   * candidate. Falls back to the diagnosis's own top-level `scope` when the
   * diagnosis candidate itself doesn't carry one (backward compat with a
   * pre-C2 diagnosis shape) so this field is always populated. This is the
   * scope a PICK OF THIS CANDIDATE would declare — never `term.scope`,
   * which is only the pool the diagnosis itself resolved against.
   */
  scope: string
}

export interface TerminologySelectionTerm {
  /** The prompt's own undefined term, verbatim from the diagnosis. */
  term: string
  /** Ranked, ≤4 — the diagnosis's own candidate order is preserved. */
  candidates: TerminologySelectionCandidate[]
  /**
   * The scope a pick for this term would declare. Always equal to the
   * diagnosis's top-level `scope` (candidates carry no scope of their own —
   * they came from whichever pool the diagnosis resolved against). May be
   * `"platform"`, in which case there is no app scope to declare; the field
   * still reports it as-is rather than inventing one.
   */
  scope: string
}

export interface TerminologySelectionModel {
  /** Same value as each term's `scope` — surfaced once at the model level. */
  scope: string
  /** Only the undefined terms that have at least one candidate. */
  terms: TerminologySelectionTerm[]
}

/**
 * Decide whether `diagnosis` warrants a terminology-selection card, and if
 * so, build the bounded, renderable model for it.
 *
 * Returns `null` when there is nothing to pick: no undefined terms, or every
 * undefined term has zero candidates. This is the COMMON case — the normal
 * silently-resolved path where nothing should be shown.
 */
export function buildTerminologySelectionModel(
  diagnosis: TerminologyDiagnosis,
): TerminologySelectionModel | null {
  const candidateBearing = diagnosis.undefinedTerms.filter(
    (undefinedTerm) => undefinedTerm.candidates.length > 0,
  )
  if (candidateBearing.length === 0) return null

  const terms: TerminologySelectionTerm[] = candidateBearing
    .slice(0, MAX_TERMS)
    .map((undefinedTerm) => ({
      term: undefinedTerm.term,
      candidates: undefinedTerm.candidates
        .slice(0, MAX_CANDIDATES_PER_TERM)
        .map((candidate) => ({
          id: candidate.id,
          kind: candidate.kind,
          score: candidate.score,
          scope: (candidate as { scope?: string }).scope ?? diagnosis.scope,
        })),
      scope: diagnosis.scope,
    }))

  return { scope: diagnosis.scope, terms }
}
